import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from './icons';

interface CameraCaptureProps {
  onCapture: (file: File) => void;
  onClose: () => void;
}

const CameraCapture: React.FC<CameraCaptureProps> = ({ onCapture, onClose }) => {
  const [hasCamera, setHasCamera] = useState(false);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Detect if the device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor;
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      setIsMobile(isMobileDevice);
      console.log('Device detection in CameraCapture:', isMobileDevice ? 'Mobile' : 'Desktop');
    };
    
    checkMobile();
  }, []);
  
  // Check if camera is available
  useEffect(() => {
    const checkCamera = async () => {
      try {
        console.log('Checking for camera devices...');
        
        // Check if mediaDevices API is supported
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
          console.error('MediaDevices API not supported');
          setCameraError('您的浏览器不支持摄像头功能');
          setHasCamera(false);
          return;
        }
        
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        console.log('Camera devices found:', videoDevices.length);
        setHasCamera(videoDevices.length > 0);
        
        if (videoDevices.length === 0) {
          console.error('No camera found');
          setCameraError('未检测到摄像头设备');
          onClose(); // Close the camera modal if no camera is available
        }
      } catch (error) {
        console.error('Error checking camera:', error);
        setHasCamera(false);
        setCameraError('检查摄像头时出错');
        // Don't close automatically, allow user to retry
      }
    };
    
    checkCamera();
  }, [onClose]);
  
  // Define startCamera with useCallback to prevent unnecessary re-creation
  const startCamera = useCallback(async () => {
    try {
      setCameraError(null);
      
      // Check permission first
      const hasPermission = await checkCameraPermission();
      if (!hasPermission) {
        return;
      }
      
      console.log('Requesting camera access...');
      
      // Configure camera constraints based on device type
      const constraints = {
        video: isMobile
          ? {
              facingMode: { ideal: 'environment' }, // Prefer back camera on mobile
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }
          : {
              facingMode: 'user', // Front camera on desktop
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }
      };
      
      console.log('Using camera constraints:', JSON.stringify(constraints));
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      console.log('Camera access granted, setting up video stream');
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          console.log('Video metadata loaded');
          if (videoRef.current) {
            videoRef.current.play()
              .then(() => {
                console.log('Video playback started');
              })
              .catch(err => {
                console.error('Error starting video playback:', err);
                setCameraError('启动视频播放时出错');
              });
          }
        };
        setIsCameraActive(true);
        console.log('Camera is now active');
      } else {
        console.error('Video ref is not available');
        setCameraError('视频元素不可用');
      }
    } catch (error: any) {
      console.error('Error starting camera:', error);
      setHasCamera(false);
      
      // Set a more user-friendly error message based on the error
      if (error.name === 'NotAllowedError') {
        setCameraError('摄像头访问被拒绝。请允许浏览器访问您的摄像头。');
      } else if (error.name === 'NotFoundError') {
        setCameraError('未找到摄像头设备。请确保您的设备有摄像头并且没有被其他应用程序使用。');
      } else if (error.name === 'NotReadableError') {
        setCameraError('无法访问摄像头。摄像头可能正被其他应用程序使用。');
      } else if (error.name === 'OverconstrainedError') {
        setCameraError('摄像头不支持请求的分辨率或功能。');
        // Try again with simpler constraints
        retryWithSimpleConstraints();
      } else {
        setCameraError(`启动摄像头时出错: ${error.message || '未知错误'}`);
      }
    }
  }, [isMobile, setCameraError, setHasCamera, setIsCameraActive]); // Add dependencies used in the function
  
  // Start camera when component mounts
  useEffect(() => {
    console.log('Camera state:', { hasCamera, isCameraActive, capturedImage });
    if (hasCamera && !isCameraActive && !capturedImage) {
      console.log('Starting camera...');
      startCamera();
    }
    
    // Clean up camera when component unmounts
    return () => {
      if (isCameraActive) {
        console.log('Cleaning up camera on unmount');
        stopCamera();
      }
    };
  }, [hasCamera, isCameraActive, capturedImage, startCamera]); // Added startCamera as a dependency
  
  const checkCameraPermission = async () => {
    try {
      // This will prompt for permission if not already granted
      const permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
      console.log('Camera permission status:', permissionStatus.state);
      
      if (permissionStatus.state === 'denied') {
        setCameraError('摄像头权限被拒绝。请在浏览器设置中允许访问摄像头。');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error checking camera permission:', error);
      // Some browsers don't support the permissions API, so we'll try to access the camera directly
      return true;
    }
  };
  
  const retryWithSimpleConstraints = async () => {
    try {
      console.log('Retrying with simple constraints');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: true // Just request any video
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          if (videoRef.current) {
            videoRef.current.play()
              .then(() => console.log('Video playback started with simple constraints'))
              .catch(err => console.error('Error starting video playback with simple constraints:', err));
          }
        };
        setIsCameraActive(true);
        setCameraError(null);
      }
    } catch (error) {
      console.error('Error with simple constraints:', error);
      setCameraError('无法访问摄像头，请检查权限设置或尝试使用其他浏览器。');
    }
  };
  
  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      console.log('Stopping camera...');
      const stream = videoRef.current.srcObject as MediaStream;
      const tracks = stream.getTracks();
      
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setIsCameraActive(false);
      setIsVideoReady(false);
      console.log('Camera stopped');
    }
  };
  
  const handleVideoReady = () => {
    console.log('Video is ready for capture');
    setIsVideoReady(true);
  };
  
  const captureImage = () => {
    console.log('Capture image button clicked');
    if (!isVideoReady) {
      console.error('Video is not ready for capture yet');
      return;
    }
    
    if (videoRef.current && canvasRef.current) {
      console.log('Video and canvas refs available');
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Check if video is ready
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        console.error('Video dimensions are zero, video may not be ready');
        return;
      }
      
      console.log('Video dimensions:', video.videoWidth, 'x', video.videoHeight);
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw video frame to canvas
      const context = canvas.getContext('2d');
      if (context) {
        console.log('Drawing video to canvas');
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Convert canvas to data URL
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9); // Higher quality for medical images
        setCapturedImage(dataUrl);
        console.log('Image captured and set to state');
        
        // Convert data URL to File object
        canvas.toBlob((blob) => {
          if (blob) {
            console.log('Created blob from canvas');
            const file = new File([blob], 'camera-capture.jpg', { type: 'image/jpeg' });
            setSelectedFile(file);
            console.log('File object created and set to state');
          } else {
            console.error('Failed to create blob from canvas');
          }
        }, 'image/jpeg', 0.9); // Higher quality for medical images
        
        // Stop camera after capture
        stopCamera();
      } else {
        console.error('Failed to get canvas context');
      }
    } else {
      console.error('Video or canvas ref is not available for capture');
    }
  };
  
  const retakePhoto = () => {
    console.log('Retaking photo');
    setCapturedImage(null);
    setSelectedFile(null);
    startCamera();
  };
  
  const handleSubmit = () => {
    console.log('Submitting captured image');
    if (selectedFile) {
      onCapture(selectedFile);
    } else {
      console.error('No file selected for submission');
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-75 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">拍摄医疗影像</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">关闭</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="p-4">
          <div className="flex flex-col items-center">
            {!capturedImage ? (
              <>
                <div className="bg-black rounded-lg overflow-hidden w-full aspect-[4/3] relative">
                  {cameraError ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800 text-white p-4">
                      <p className="text-center mb-4">{cameraError}</p>
                      <button
                        onClick={startCamera}
                        className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        重试
                      </button>
                    </div>
                  ) : (
                    <>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted // Important for iOS
                        className="w-full h-full object-cover"
                        onLoadedMetadata={() => console.log('Video metadata loaded')}
                        onCanPlay={handleVideoReady}
                      />
                      {!isVideoReady && isCameraActive && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 text-white">
                          <p>相机正在加载中...</p>
                        </div>
                      )}
                    </>
                  )}
                  <canvas ref={canvasRef} className="hidden" />
                </div>
                {!cameraError && (
                  <button
                    onClick={captureImage}
                    disabled={!isVideoReady}
                    className={`mt-4 px-4 py-2 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      isVideoReady 
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700' 
                        : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    }`}
                  >
                    <div className="flex items-center">
                      <Icon iconName="OutlineCamera" size={20} color="currentColor" />
                      <span className="ml-2">拍照</span>
                    </div>
                  </button>
                )}
              </>
            ) : (
              <>
                <div className="bg-black rounded-lg overflow-hidden w-full aspect-[4/3]">
                  <img
                    src={capturedImage}
                    alt="Captured"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex mt-4 space-x-4">
                  <button
                    onClick={retakePhoto}
                    className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    重新拍照
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    使用此照片
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CameraCapture; 